// app/components/Header.tsx
import React, { memo, useState, useEffect } from "react";
import { NavLink, useNavigate, useSubmit, useLocation } from "@remix-run/react";
import { toast } from "sonner";
import { Button } from "~/components/general/Button";
import { Avatar, AvatarFallback } from "~/components/general/Avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { Contact } from "~/utils/schema/schema";
import logo from "~/assets/logo.svg";
import { useTranslation } from "react-i18next";
import { Bell, CheckCheck, User, FileText, AlignJustify, ChartBarIncreasing } from "lucide-react";
import axios from "axios";

interface Notification {
  id: string;
  creationDate: string;
  updateDate: string;
  deletedAt: string | null;
  title: string;
  description: string;
  type: string;
  isRead: boolean;
  createdById: string;
  createdByName: string;
  receiverId: string;
  receiverName: string;
  link: string;
}

interface HeaderProps {
  contact: Contact | undefined;
  notifications?: Notification[];
  onMarkAllAsRead?: () => void;
  userRole?: number;
}

const Header: React.FC<HeaderProps> = memo(
  ({ contact, notifications = [], onMarkAllAsRead }): JSX.Element => {
    const submit = useSubmit();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [notificationsState, setNotificationsState] =
      React.useState<Notification[]>(notifications);
    const [notificationPopoverOpen, setNotificationPopoverOpen] =
      React.useState(false);
    const [isOpenSidebar, setIsOpenSidebar] = React.useState(false);
    const location = useLocation();

    React.useEffect(() => {
      setNotificationsState(notifications);
    }, [notifications]);

    useEffect(() => {
      setIsOpenSidebar(false);
    }, [location.pathname]);

    const handleLogout = () => {
      toast.success("Logout Successful", {
        dismissible: true,
        duration: 1500,
      });
      submit(null, { action: "/logout", method: "post" });
    };

    const unreadCount = notificationsState.filter(
      (note) => !note.isRead
    ).length;
    const formatDate = (dateString: string) => {
      try {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }).format(date);
      } catch (e) {
        console.error("Error formatting date:", e);
        return dateString;
      }
    };

    const groupNotificationsByDate = (notifications: Notification[]) => {
      const today = new Date().setHours(0, 0, 0, 0);
      const yesterday = new Date(today - 86400000).setHours(0, 0, 0, 0);

      return notifications.reduce((groups, notification) => {
        const date = new Date(notification.creationDate).setHours(0, 0, 0, 0);

        let group = t("notifications.older");
        if (date === today) {
          group = t("notifications.today");
        } else if (date === yesterday) {
          group = t("notifications.yesterday");
        }

        if (!groups[group]) {
          groups[group] = [];
        }

        groups[group].push(notification);
        return groups;
      }, {} as Record<string, Notification[]>);
    };

    const markNotificationsAsRead = async (notificationIds: string[]) => {
      if (notificationIds.length === 0) return;

      try {
        const formData = new FormData();
        formData.append("isRead", "true");
        formData.append("isReceiverRead", "true");
        notificationIds.forEach((id) => {
          formData.append("notificationIds[]", id);
        });

        const response = await axios.post(`/api/read-notifications`, formData, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });

        if (response.status === 200) {
          setNotificationsState((prev) =>
            prev.map((note) =>
              notificationIds.includes(note.id)
                ? { ...note, isRead: true }
                : note
            )
          );
        } else {
          console.error("Error updating notification status:", response.data);
        }
      } catch (error) {
        console.error("Error updating notification status:", error);
      }
    };

    const handleNotificationClick = async (notification: Notification) => {
      setNotificationPopoverOpen(false);

      let navigationPromise = Promise.resolve();

      if (!notification.isRead) {
        setNotificationsState((prev) =>
          prev.map((note) =>
            note.id === notification.id ? { ...note, isRead: true } : note
          )
        );

        try {
          const formData = new FormData();
          formData.append("isRead", "true");
          formData.append("isReceiverRead", "true");

          navigationPromise = axios
            .post(`/api/read-notify/${notification.id}`, formData, {
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
            })
            .then((response) => {
              if (response.status !== 200) {
                console.error(
                  "Error updating notification status:",
                  response.data
                );
                setNotificationsState((prev) =>
                  prev.map((note) =>
                    note.id === notification.id
                      ? { ...note, isRead: false }
                      : note
                  )
                );
              }
            })
            .catch((error) => {
              console.error("Error updating notification status:", error);
              setNotificationsState((prev) =>
                prev.map((note) =>
                  note.id === notification.id
                    ? { ...note, isRead: false }
                    : note
                )
              );
            });
        } catch (error) {
          console.error("Unexpected error:", error);
        }
      }

      if (notification.link) {
        await navigationPromise;
        navigate(notification.link);
      }
    };

    const handleMarkAllAsRead = async () => {
      const unreadNotifications = notificationsState.filter(
        (note) => !note.isRead
      );
      const unreadIds = unreadNotifications.map((note) => note.id);
      if (unreadIds.length > 0) {
        await markNotificationsAsRead(unreadIds);
      }

      if (onMarkAllAsRead) {
        onMarkAllAsRead();
      }

      toast.success(
        t("notifications.markedAllAsRead") ||
          "All notifications marked as read!",
        {
          duration: 2000,
        }
      );
    };

    const getNotificationIcon = (type: string) => {
      switch (type.toLowerCase()) {
        case "contract":
          return <FileText className="w-5 h-5 text-gray-500" />;
        case "partner":
          return <User className="w-5 h-5 text-gray-500" />;
        default:
          return <Bell className="w-5 h-5 text-gray-500" />;
      }
    };

    const groupedNotifications = groupNotificationsByDate(notificationsState);
    const renderFormattedDescription = (description: string) => {
      let formattedText = description;
      const parts = formattedText.split(/\s+/);

      return (
        <>
          {parts.map((part, index) => {
            const isName =
              /^[A-Z][a-z]+ [A-Z][a-z]+$/.test(part) ||
              /^[A-Z][a-z]+ \d+$/.test(part);
            const isAmount = /^\$[\d,\.]+$/.test(part);

            if (isName || isAmount) {
              return (
                <strong key={index}>
                  {part}
                  {index < parts.length - 1 ? " " : ""}
                </strong>
              );
            }

            return (
              <span key={index}>
                {part}
                {index < parts.length - 1 ? " " : ""}
              </span>
            );
          })}
        </>
      );
    };

    return (
      <header className="sticky top-0 z-50 w-full border-b flex flex-row items-center justify-between bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        
        <div className="ml-3 md:hidden cursor-pointer" onClick={() => setIsOpenSidebar(!isOpenSidebar)}>
          <Button variant="ghost" className="relative h-12 w-12 rounded-full flex items-center justify-center">
            <AlignJustify className="h-9 w-9" />
          </Button>
        </div>

        <div className={`absolute md:hidden top-16 left-0 w-full bg-white flex flex-col items-center text-xs transform transition-transform ${isOpenSidebar ? "opacity-100" : "opacity-0 pointer-events-none"}`} style={{transition: "transform 0.3s ease, opacity 0.3s ease"}}>
          <NavLink className="w-full" to="/"><li className="flex text-md items-center gap-2 list-none w-full p-2 px-8 hover:bg-slate-300 hover:text-slate-900 transition-all cursor-pointer"> <ChartBarIncreasing className="h-4 w-4 flex-shrink-0" /> <span className="font-bold text-sm">{t("navigation.dashboard")}</span></li></NavLink>
          <NavLink className="w-full" to="/contract"><li className="flex text-md items-center gap-2 list-none w-full p-2 px-8 hover:bg-slate-300 hover:text-slate-900 transition-all cursor-pointer"> <FileText className="h-4 w-4 flex-shrink-0" /> <span className="font-bold text-sm">{t("navigation.contracts")}</span></li></NavLink>
        </div>
        
        <div
          onClick={() => navigate("/")}
          className={"cursor-pointer flex self-center p-2 ml-5"}
        >
          <img src={logo} alt="SGB Logo" className="min-h-10 min-w-10 md:min-h-14 md:min-w-14" />
        </div>

        <div className="flex items-center justify-end pr-5 space-x-3">
          <Popover
            open={notificationPopoverOpen}
            onOpenChange={setNotificationPopoverOpen}
          >
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className="relative h-12 w-12 rounded-full flex items-center justify-center"
              >
                <Bell className="h-9 w-9" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[450px] shadow-xl rounded-lg p-0"
              align="end"
              forceMount
            >
              <div className="flex flex-col divide-y">
                <div className="p-4 flex items-center justify-between border-b">
                  <span className="font-semibold text-lg">
                    {t("notifications.title") || "Notifications"}
                  </span>
                  <button
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-1"
                    onClick={handleMarkAllAsRead}
                  >
                    <CheckCheck className="h-4 w-4" />
                    <span>Mark all as read</span>
                  </button>
                </div>

                {notificationsState.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {t("notifications.empty") || "No notifications"}
                  </div>
                ) : (
                  <div className="max-h-[500px] overflow-y-auto">
                    {Object.entries(groupedNotifications).map(
                      ([date, items]) => (
                        <div key={date}>
                          <div className="bg-gray-50 px-4 py-2 font-medium text-gray-500 text-sm">
                            {date}
                          </div>

                          {items.map((notification) => (
                            <div
                              key={notification.id}
                              className={`p-4 hover:bg-gray-100 cursor-pointer border-b ${
                                !notification.isRead ? "bg-blue-200/30" : ""
                              } overflow-hidden`}
                              onClick={() =>
                                handleNotificationClick(notification)
                              }
                            >
                              <div className="flex items-start gap-3 w-full overflow-hidden">
                                <div className="p-2 bg-gray-100 rounded-full flex-shrink-0">
                                  {getNotificationIcon(notification.type)}
                                </div>

                                <div className="flex-1 min-w-0 overflow-hidden">
                                  <div className="flex items-start justify-between w-full">
                                    <div className="flex items-center max-w-[75%]">
                                      {!notification.isRead && (
                                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 flex-shrink-0"></span>
                                      )}
                                      <h4 className="text-sm font-semibold text-gray-900 truncate">
                                        {notification.title}
                                      </h4>
                                    </div>
                                    <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                                      {formatDate(notification.creationDate)}
                                    </span>
                                  </div>

                                  <div className="text-sm text-gray-600 mt-1 whitespace-normal break-words overflow-hidden overflow-wrap-anywhere max-w-full">
                                    {renderFormattedDescription(
                                      notification.description
                                    )}
                                  </div>
                                  <div className="text-sm flex flex-row text-gray-600 mt-1 whitespace-normal break-words overflow-hidden overflow-wrap-anywhere max-w-full">
                                    <div className="font-semibold pr-2">
                                      From:{" "}
                                    </div>{" "}
                                    {notification.createdByName}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                )}

                <div className="p-4 text-center">

                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className="relative h-12 w-12 rounded-full"
              >
                {contact && (
                  <Avatar>
                    <AvatarFallback>
                      {contact.firstName ? contact.firstName.charAt(0) : ""}
                      {contact.lastName ? contact.lastName.charAt(0) : ""}
                    </AvatarFallback>
                  </Avatar>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-56 shadow-2xl rounded-md items-center justify-center p-5 gap-5"
              align="center"
              forceMount
            >
              <div className="flex flex-row gap-2 mb-5 w-full">
                <span className="font-medium">{contact?.firstName || ""}</span>
                <span className="font-medium">{contact?.lastName || ""}</span>
              </div>
              <Button onClick={handleLogout} className={"w-full"}>
                {t("navigation.logout")}
              </Button>
            </PopoverContent>
          </Popover>
        </div>
      </header>
    );
  }
);

export default Header;
